// The words around every page: the top bar, the page footer, the legal bar and
// the language switcher.
//
// The English values are the ones the site has always shown, and the
// components fall back to them wherever no translated copy is in the page data
// — every English page, the cabinet and the console included. A translated
// route loads its language's copy in its layout, so the bar, the footer and
// the switcher read in the same language as the page between them.

import { fromLocale } from "../load";
import type { TranslatedLocale } from "../locales";

export interface ChromeCopy {
  /** The top bar's sections, by the id the shared definition gives each one. */
  readonly nav: {
    readonly "passport-ocr": string;
    readonly "free-api": string;
    readonly mcp: string;
    readonly compare: string;
    readonly tools: string;
    readonly docs: string;
    readonly pricing: string;
    readonly blog: string;
    readonly status: string;
  };
  /** The accessible name of the top bar's navigation. */
  readonly navLabel: string;
  readonly signIn: string;
  readonly register: string;
  readonly cabinet: string;
  /** The footer at the end of every marketing page. */
  readonly footer: {
    readonly freeTools: string;
    readonly mrzParser: string;
    readonly mrzGenerator: string;
    readonly fieldExplorer: string;
    readonly guides: string;
    readonly freeApi: string;
    readonly mrzFormats: string;
    readonly mcpServer: string;
    readonly compare: string;
    readonly byCountry: string;
    /** A country page's link text; `{country}` is replaced with the country's name. */
    readonly countryPage: string;
    readonly blog: string;
    readonly documentation: string;
    readonly pricing: string;
    readonly status: string;
    readonly company: string;
    readonly about: string;
    readonly contact: string;
    readonly account: string;
    readonly signIn: string;
    readonly createAccount: string;
    readonly liveDemo: string;
  };
  /** The legal bar under every page. */
  readonly legal: {
    /** The accessible name of the bar. */
    readonly label: string;
    readonly terms: string;
    readonly privacy: string;
    readonly "data-processing": string;
    readonly cookieSettings: string;
    /**
     * One sentence saying the legal texts are English and the English governs.
     * Shown on translated pages only; empty on English ones.
     */
    readonly englishOnly: string;
  };
  /** The language switcher. */
  readonly language: {
    /** The visible label of the switcher and the accessible name of its list. */
    readonly label: string;
  };
}

/** The English words, exactly as the components printed them before any translation existed. */
export const CHROME_EN: ChromeCopy = {
  nav: {
    "passport-ocr": "Passport OCR",
    "free-api": "Free API",
    mcp: "MCP",
    compare: "Compare",
    tools: "Tools",
    docs: "Docs",
    pricing: "Pricing",
    blog: "Blog",
    status: "Status",
  },
  navLabel: "Primary",
  signIn: "Sign in",
  register: "Create account",
  cabinet: "Cabinet",
  footer: {
    freeTools: "Free tools",
    mrzParser: "MRZ parser",
    mrzGenerator: "MRZ generator",
    fieldExplorer: "Document field explorer",
    guides: "Guides",
    freeApi: "Free passport OCR API",
    mrzFormats: "TD1, TD2 and TD3 formats",
    mcpServer: "MCP server",
    compare: "How doc.cheap compares",
    byCountry: "Passport OCR by country",
    countryPage: "{country} passport OCR",
    blog: "Blog",
    documentation: "Documentation",
    pricing: "Pricing",
    status: "Status",
    company: "Company",
    about: "About",
    contact: "Contact",
    account: "Account",
    signIn: "Sign in",
    createAccount: "Create account",
    liveDemo: "Live demo",
  },
  legal: {
    label: "Legal",
    terms: "Terms",
    privacy: "Privacy",
    "data-processing": "Data processing",
    cookieSettings: "Cookie settings",
    englishOnly: "",
  },
  language: {
    label: "Language",
  },
};

const modules = import.meta.glob("../locales/*/chrome.ts");

export function loadChromeCopy(locale: TranslatedLocale): Promise<ChromeCopy> {
  return fromLocale<ChromeCopy>(modules, locale, "chrome");
}

/** The chrome copy a page's data carries, or English when it carries none. */
export function chromeOf(data: Record<string, unknown>): ChromeCopy {
  const chrome = data["chrome"];
  return chrome !== null && typeof chrome === "object" ? (chrome as ChromeCopy) : CHROME_EN;
}
